var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import FetchProvider from './FetchProvider';
import { NamingService } from './NamingService';
import ConfigurationError, { ConfigurationErrorCode } from './errors/configurationError';
import ResolutionError, { ResolutionErrorCode } from './errors/resolutionError';
import { AccountRecordTypes, } from './types/AccountData';
import { DasSupportedNetwork } from './types/index';
import { NamingServiceName, } from './types/publicTypes';
export class DasService extends NamingService {
    constructor(source = {}) {
        super();
        this.name = NamingServiceName.DAS;
        if (!(source.url || source.provider)) {
            throw new ConfigurationError(ConfigurationErrorCode.UnspecifiedUrl, {
                method: NamingServiceName.DAS
            });
        }
        if (!source.network) {
            source.network = 'mainnet';
        }
        if (!DasSupportedNetwork.guard(source.network)) {
            throw new ConfigurationError(ConfigurationErrorCode.UnsupportedNetwork, {
                method: NamingServiceName.DAS,
            });
        }
        this.network = source.network;
        this.url = source.url;
        this.provider = source.provider || new FetchProvider(this.name, this.url);
    }
    static autonetwork(source) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!source) {
                source = {
                    url: DasService.UrlMap['mainnet'],
                    network: 'mainnet'
                };
            }
            else if (source.network && !source.url) {
                source.url = DasService.UrlMap[source.network];
            }
            return new DasService(source);
        });
    }
    serviceName() {
        return this.name;
    }
    isSupportedDomain(account) {
        return /.+\.bit/.test(account) && account.split('.').every(v => Boolean(v.length));
    }
    // todo: implement namehash
    namehash(account) {
        if (!this.isSupportedDomain(account)) {
            throw new ResolutionError(ResolutionErrorCode.UnregisteredDomain, {
                domain: account,
            });
        }
        return '';
    }
    childhash(_parentHash, label) {
        throw new ResolutionError(ResolutionErrorCode.UnsupportedMethod, {
            domain: label,
            methodName: 'childhash'
        });
    }
    owner(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountData = yield this.getAccountData(account);
            return accountData.owner_lock_args_hex;
        });
    }
    resolver(account) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new ResolutionError(ResolutionErrorCode.UnsupportedMethod, {
                domain: account,
                methodName: 'resolver',
            });
        });
    }
    record(account, key) {
        return __awaiter(this, void 0, void 0, function* () {
            key = key.toLowerCase();
            const returnee = (yield this.allRecords(account))[key];
            if (!returnee) {
                throw new ResolutionError(ResolutionErrorCode.RecordNotFound, {
                    domain: account,
                    recordName: key,
                });
            }
            return returnee;
        });
    }
    records(account, keys) {
        return __awaiter(this, void 0, void 0, function* () {
            const records = yield this.allRecords(account);
            return keys.reduce((returnee, key) => {
                returnee[key] = records[key] || '';
                return returnee;
            }, {});
        });
    }
    allRecords(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.getAccountData(account);
            if (data) {
                const records = data.records;
                const returnee = {};
                records.forEach(record => {
                    returnee[record.key.toLowerCase()] = record.value;
                });
                return returnee;
            }
            // no records
            else {
                return {};
            }
        });
    }
    reverse(address, currencyTicker) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!['ETH', 'CKB'].includes(currencyTicker)) {
                throw new Error('Das does not support any chain other than CKB and ETH');
            }
            const accounts = yield this.allReverse(address);
            return accounts[0];
        });
    }
    isRegistered(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountData = yield this.getAccountData(account);
            return Boolean(accountData);
        });
    }
    twitter(account) {
        throw new ResolutionError(ResolutionErrorCode.UnsupportedMethod, {
            domain: account,
            methodName: 'twitter'
        });
    }
    /* -------- custom methods --------- */
    /**
     * return all the records for the give key
     * @param account
     * @param key
     */
    recordsByKey(account, key) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountData = yield this.getAccountData(account);
            return yield accountData.records.filter(record => record.key === key);
        });
    }
    addr(account, ticker) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.record(account, `address.${ticker}`);
        });
    }
    account(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountData = yield this.getAccountData(account);
            const profiles = accountData.records.filter(record => record.type === AccountRecordTypes.profile);
            const addresses = accountData.records.filter(record => record.type === AccountRecordTypes.address);
            const dwebs = accountData.records.filter(record => record.type === AccountRecordTypes.dweb);
            const customs = accountData.records.filter(record => record.type === AccountRecordTypes.custom);
            function resolveOneFromRecords(records) {
                return records.reduce((profile, record) => {
                    profile[record.strippedKey] = record;
                    return profile;
                }, {});
            }
            return {
                account: accountData.account,
                avatar: `https://identicons.da.systems/identicon/${account}`,
                profile: resolveOneFromRecords(profiles),
                address: resolveOneFromRecords(addresses),
                dweb: resolveOneFromRecords(dwebs),
                custom: resolveOneFromRecords(customs),
                profiles,
                addresses,
                dwebs,
                customs,
                records: accountData.records,
            };
        });
    }
    allReverse(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.provider.request({
                method: 'das_getAddressAccount',
                params: [address],
            });
            const accounts = data.data.account_data;
            return accounts.map(account => account.account);
        });
    }
    getAccountData(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.provider.request({
                method: 'das_searchAccount',
                params: [
                    account,
                ]
            });
            if (!data.data) {
                throw new ResolutionError(ResolutionErrorCode.UnregisteredDomain, {
                    domain: account,
                });
            }
            data.data.account_data.records.forEach(record => {
                // the raw data is string, '300'
                record.ttl = Number(record.ttl);
                const keyParts = record.key.split('.');
                const type = keyParts.shift();
                const strippedKey = keyParts.join('.');
                record.type = type;
                record.strippedKey = strippedKey;
            });
            return data.data.account_data;
        });
    }
}
DasService.UrlMap = {
    'mainnet': 'https://indexer.da.systems',
    'testnet': 'http://47.243.90.165:8223',
    'aggron': 'http://47.243.90.165:8223',
};
//# sourceMappingURL=data:application/json;base64,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